/**
 * Menu — a kit popover of actions or links with menu semantics (L1 app menus, the user menu,
 * Export). The popup carries data-kit="popover" data-variant="menu"; items are role="menuitem".
 *
 * R-16 T-135 (v1.2 change 02): `hover` opens on hover intent (150ms, --menu-open-delay), switches at once to a
 * neighbour in the same MenuBarIntent, closes 280ms (--menu-close-grace) after the pointer leaves trigger and popup,
 * and pins on a click, tap, Enter, Space or ↓ (rules in ./menu-model.ts). Without `hover` (table and row menus) it
 * stays click-only. `mega` lays the popup out in grouped columns (MenuGroup); MenuFlyout is the one flyout level (›).
 */
import * as React from 'react';
import { Menu as BaseMenu } from '@base-ui-components/react/menu';
import { cssMs, MenuIntent } from './menu-model';
import './menu.css';

const IntentContext = React.createContext<MenuIntent | null>(null);

function readDelays() {
  if (typeof window === 'undefined') return { open: 150, grace: 280 };
  const s = getComputedStyle(document.documentElement);
  return { open: cssMs(s.getPropertyValue('--menu-open-delay'), 150), grace: cssMs(s.getPropertyValue('--menu-close-grace'), 280) };
}

/** A bar of hover menus (the app bar, the platform bar's ▾ menus): one open at a time, instant switch between them. */
export function MenuBarIntent({ children }: { children: React.ReactNode }) {
  const [intent] = React.useState(() => new MenuIntent(readDelays));
  return <IntentContext.Provider value={intent}>{children}</IntentContext.Provider>;
}

interface MenuProps {
  trigger: React.ReactElement;
  children: React.ReactNode;
  align?: 'start' | 'center' | 'end';
  'aria-label'?: string;
  /** v1.2: open on hover intent (default false: click-only, as table and row menus stay) */
  hover?: boolean;
  /** v1.2: the popup is a mega menu of grouped columns (MenuGroup children) */
  mega?: boolean;
  /** extra elements whose hover keeps this menu open or opens it (the L0 link beside its ▾) */
  hoverRef?: React.RefObject<HTMLElement | null>;
  'data-testid'?: string;
}

export function Menu({ trigger, children, align = 'start', 'aria-label': label, hover = false, mega = false, hoverRef, ...rest }: MenuProps) {
  const popup = (
    <BaseMenu.Portal>
      <BaseMenu.Positioner align={align} sideOffset={4} collisionPadding={8} className="k-pos">
        <HoverPopup className={mega ? 'k-pop k-mega' : 'k-pop'} mega={mega} label={label} testId={rest['data-testid']}>
          {children}
        </HoverPopup>
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  );
  if (!hover)
    return (
      <BaseMenu.Root>
        <BaseMenu.Trigger render={trigger} />
        {popup}
      </BaseMenu.Root>
    );
  return (
    <HoverMenu trigger={trigger} hoverRef={hoverRef}>
      {popup}
    </HoverMenu>
  );
}

/** Hover intent is for a pointer that hovers; touch opens by tap (a press). */
const HOVERING = new Set(['mouse', 'pen']);
const isPointer = (e: React.PointerEvent | PointerEvent) => HOVERING.has(e.pointerType);
/** Base UI's open-change reasons: a press on the trigger (or ↓ from it) pins; hover and focus moves are ours to decide. */
const PRESS = new Set(['trigger-press']);
const OPEN_BY_KEY = new Set(['list-navigation']);
const OURS = new Set(['trigger-hover', 'focus-out']);
const FOCUS_OUT = new Set(['focus-out']);

function HoverMenu({ trigger, children, hoverRef }: { trigger: React.ReactElement; children: React.ReactElement; hoverRef?: React.RefObject<HTMLElement | null> }) {
  const id = React.useId();
  const group = React.useContext(IntentContext);
  const [own] = React.useState(() => (group ? null : new MenuIntent(readDelays)));
  const intent = (group ?? own)!;
  const open = React.useSyncExternalStore(
    (cb) => intent.subscribe(cb),
    () => intent.open === id,
    () => false,
  );
  React.useEffect(() => {
    const el = hoverRef?.current;
    if (!el) return;
    // a click on the link navigates: the menu closes and stays shut while the pointer rests there
    let quiet = false;
    const enter = (e: PointerEvent) => isPointer(e) && !quiet && intent.enter(id);
    const leave = (e: PointerEvent) => {
      quiet = false;
      if (isPointer(e)) intent.leave(id);
    };
    const click = () => {
      quiet = true;
      intent.dismiss();
    };
    el.addEventListener('pointerenter', enter);
    el.addEventListener('pointerleave', leave);
    el.addEventListener('click', click);
    return () => {
      el.removeEventListener('pointerenter', enter);
      el.removeEventListener('pointerleave', leave);
      el.removeEventListener('click', click);
    };
  }, [hoverRef, intent, id]);
  React.useEffect(() => () => {
    if (intent.open === id) intent.dismiss();
  }, [intent, id]);
  const pointer = {
    onPointerEnter: (e: React.PointerEvent) => isPointer(e) && intent.enter(id),
    onPointerLeave: (e: React.PointerEvent) => isPointer(e) && intent.leave(id),
  };
  return (
    <BaseMenu.Root
      open={open}
      modal={false}
      onOpenChange={(next, details) => {
        // a press on the trigger (click, tap, Enter, Space) or ↓ from it pins; any other close dismisses
        if (PRESS.has(details.reason) || (next && OPEN_BY_KEY.has(details.reason))) intent.press(id);
        else if (!next && !OURS.has(details.reason)) intent.dismiss();
        else if (!next && FOCUS_OUT.has(details.reason) && intent.pinned) intent.dismiss();
      }}
    >
      <BaseMenu.Trigger render={trigger} {...pointer} />
      <HoverPopupContext.Provider value={pointer}>{children}</HoverPopupContext.Provider>
    </BaseMenu.Root>
  );
}

const HoverPopupContext = React.createContext<{ onPointerEnter: (e: React.PointerEvent) => void; onPointerLeave: (e: React.PointerEvent) => void } | null>(null);

/** The popup; in a hover menu the pointer entering it keeps the menu open (the diagonal's end), leaving it starts the grace. */
function HoverPopup({ className, mega, label, testId, children }: { className: string; mega: boolean; label?: string; testId?: string; children: React.ReactNode }) {
  const p = React.useContext(HoverPopupContext);
  return (
    <BaseMenu.Popup className={className} data-kit="popover" data-variant="menu" data-mega={mega || undefined} aria-label={label} data-testid={testId} onPointerEnter={p?.onPointerEnter} onPointerLeave={p?.onPointerLeave}>
      {children}
    </BaseMenu.Popup>
  );
}

/** The mega menu's columns: wraps its MenuGroup children; at most four columns (--menu-col-min … --menu-col-max). */
export function MenuColumns({ children, count }: { children: React.ReactNode; count: number }) {
  return (
    <div className="k-mega-cols" data-columns={Math.min(Math.max(count, 1), 4)}>
      {children}
    </div>
  );
}

/** One column: the group's title (none for the untitled column) over its items (role="group"). */
export function MenuGroup({ title, children }: { title: string | null; children: React.ReactNode }) {
  return (
    <BaseMenu.Group className="k-mega-col" data-testid={title ? `menu-group-${title}` : 'menu-group'}>
      {title ? <BaseMenu.GroupLabel className="k-pop-head k-mega-title">{title}</BaseMenu.GroupLabel> : null}
      {children}
    </BaseMenu.Group>
  );
}

/**
 * An action, or a link when `render` is given (e.g. `render={<Link to="/x" />}`). With `checked`
 * it is an on/off item (role menuitemcheckbox, aria-checked; R-14 the Expert mode switch) and
 * `onClick` toggles it. R-16: `hint` is one line under the label (--ink-2, truncated); `current` marks the page
 * the reader is on (aria-current="page"); `star` adds the quick-link star at the row's end (shown on hover/focus,
 * kept while filled).
 */
export function MenuItem({
  children,
  onClick,
  render,
  checked,
  hint,
  current,
  star,
  ...rest
}: {
  children: React.ReactNode;
  onClick?: () => void;
  render?: React.ReactElement;
  checked?: boolean;
  hint?: string;
  current?: boolean;
  /** `disabledWhy`: the id of the DisabledWhy line that says why the star does nothing right now (it is then disabled) */
  star?: { on: boolean; label: string; onToggle: () => void; disabledWhy?: string };
  'data-testid'?: string;
}) {
  const hintId = React.useId();
  if (checked !== undefined)
    return (
      <BaseMenu.CheckboxItem className="k-pop-item" data-kit="option" checked={checked} onCheckedChange={() => onClick?.()} closeOnClick data-testid={rest['data-testid']}>
        <span className="k-pop-check" aria-hidden>
          {checked ? '✓' : ''}
        </span>
        {children}
      </BaseMenu.CheckboxItem>
    );
  const body = hint ? (
    <span className="k-mega-text">
      <span className="k-mega-label">{children}</span>
      {/* the hint describes the item, it is not its name: "Hot list" stays the menuitem's name (rule 33 locators) */}
      <span className="k-mega-hint" id={hintId} aria-hidden>
        {hint}
      </span>
    </span>
  ) : (
    children
  );
  const item = (
    <BaseMenu.Item className="k-pop-item" data-kit="option" onClick={onClick} render={render} aria-current={current ? 'page' : undefined} aria-describedby={hint ? hintId : undefined} data-testid={rest['data-testid']}>
      {body}
    </BaseMenu.Item>
  );
  if (!star) return item;
  return (
    <div className="k-mega-row" data-starred={star.on || undefined}>
      {item}
      <BaseMenu.Item
        className="k-mega-star"
        data-kit="option"
        closeOnClick={false}
        disabled={Boolean(star.disabledWhy)}
        aria-describedby={star.disabledWhy}
        aria-label={star.on ? `Remove ${star.label} from quick links` : `Add ${star.label} to quick links`}
        data-on={star.on || undefined}
        data-testid={rest['data-testid'] ? `${rest['data-testid']}-star` : undefined}
        onClick={star.onToggle}
      >
        <span aria-hidden>{star.on ? '★' : '☆'}</span>
      </BaseMenu.Item>
    </div>
  );
}

/** The one flyout level (›), opening to the right on hover or press; never a second level. */
export function MenuFlyout({ label, children, 'data-testid': testId }: { label: React.ReactNode; children: React.ReactNode; 'data-testid'?: string }) {
  return (
    <BaseMenu.SubmenuRoot>
      <BaseMenu.SubmenuTrigger className="k-pop-item k-flyout-trigger" data-kit="option" openOnHover delay={0} closeDelay={280} data-testid={testId}>
        <span>{label}</span>
        <span className="k-flyout-mark" aria-hidden>
          ›
        </span>
      </BaseMenu.SubmenuTrigger>
      <BaseMenu.Portal>
        <BaseMenu.Positioner side="right" align="start" sideOffset={2} collisionPadding={8} className="k-pos">
          <BaseMenu.Popup className="k-pop" data-kit="popover" data-variant="menu" data-flyout>
            {children}
          </BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </BaseMenu.SubmenuRoot>
  );
}

export function MenuLabel({ children }: { children: React.ReactNode }) {
  return <div className="k-pop-head">{children}</div>;
}

export function MenuText({ children }: { children: React.ReactNode }) {
  return <div className="k-meta" style={{ padding: '4px 6px' }}>{children}</div>;
}

export function MenuSeparator() {
  return <BaseMenu.Separator className="k-pop-sep" />;
}
